import { Dispatch } from "react";
import { State } from "./state";
import { processAutoCreate } from "./BrickAutoCreate/AutoCreate";
import { Collect3005 } from "./BrickActions/Actions/Collect/Collect3005";
import { CreateBrick_3004 } from "./BrickActions/Actions/Create/CreateBrick3004";
import { Split3005into3024 } from "./BrickActions/Actions/Split/Split3005Into3024";
import { IncreaseCapacity3005 } from "./BrickActions/Actions/Capacity/IncreaseCapacity3005";
import { AutoCollect_3005 } from "./BrickActions/Actions/AutoCollect/AutoCollect3005";
import { Split3004into3023 } from "./BrickActions/Actions/Split/Split3004Into3023";
import { IncreaseCapacity3004 } from "./BrickActions/Actions/Capacity/IncreaseCapacity3004";
import { Action } from "./action";
import { IncreaseCapacity3023 } from "./BrickActions/Actions/Capacity/IncreaseCapacity3023";
import { AutoCollect_Generic } from "./BrickActions/Actions/AutoCollect/AutoCollectGeneric";
import { AutoCollect_Sets_Generic } from "./BrickActions/Actions/AutoCollect/AutoCollect_Sets_Generic";
import { AutoCollect_BrickPack_Generic } from "./BrickActions/Actions/AutoCollect/AutoCollect_BrickPack_Generic";
import { BuyBrickPack } from "./BrickActions/Actions/BuyBrickPack/BuyBrickPack";
import { UpgradeGeneric } from "./BrickActions/Actions/Upgrade/UpgradeGeneric";
import { Upgrade_Sets_Generic } from "./BrickActions/Actions/Upgrade/Upgrade_Sets_Generic";
import { CreateBrick_3010 } from "./BrickActions/Actions/Create/CreateBrick3010";
import { CreateBrick_3003 } from "./BrickActions/Actions/Create/CreateBrick3003";
import { CreateBrick_3001 } from "./BrickActions/Actions/Create/CreateBrick3001";
import { IncreaseCapacityGeneric } from "./BrickActions/Actions/Capacity/IncreaseCapacityGeneric";
import { Split3003into3022 } from "./BrickActions/Actions/Split/Split3003Into3022";
import { Split3010into3710 } from "./BrickActions/Actions/Split/Split3010Into3710";
import { Split3001into3020 } from "./BrickActions/Actions/Split/Split3001Into3020";
import {
  saveToLocalStorage,
  deserializeState,
  validateSaveData,
} from "../utils/saveLoad";
import { BuySetGeneric } from "./BrickActions/Actions/BuySet/BuySetGeneric";

// Interface for Reducer context if needed
export interface IReducer {
  state: State;
  dispatch: Dispatch<Action>;
}

// Explicitly typed reducer with React.Reducer
export const reducer: React.Reducer<State, Action> = (
  state: State,
  action: Action,
): State => {
  const { type, actionTimestamp, rowChange } = action;

  switch (type) {
    //
    //
    // AUTOCOLLECT
    //
    //

    case "autocreate_every_second":
      return processAutoCreate(state);

    //
    //
    // TIMESTAMPS
    //
    //

    case "ADD_ACTION_TIMESTAMP":
      return {
        ...state,
        actionTimestamps: actionTimestamp
          ? [...state.actionTimestamps, actionTimestamp]
          : state.actionTimestamps,
      };

    case "CLEAN_OLD_TIMESTAMPS":
      const thirtySecondsAgo = Date.now() - 30 * 1000;
      return {
        ...state,
        actionTimestamps: state.actionTimestamps.filter(
          (timestamp) => timestamp.timestamp > thirtySecondsAgo,
        ),
      };

    //
    //
    // PLAYER SETTINGS
    //
    //

    case "TOGGLE_USE_ACTION_ROWS":
      return { ...state, useActionRows: rowChange ?? state.useActionRows };
    case "TOGGLE_USE_INVENTORY_ROWS":
      return {
        ...state,
        useInventoryRows: rowChange ?? state.useInventoryRows,
      };
    case "TOGGLE_ACTION_USER_VISIBILITY": {
      // Toggle userHidden status for specific action
      if (!action.actionId) return state;

      const targetAction = state.actions.get(action.actionId);
      if (!targetAction) return state;

      const newActions = new Map(state.actions);
      newActions.set(action.actionId, {
        ...targetAction,
        userHidden: !targetAction.userHidden,
      });

      return { ...state, actions: newActions };
    }

    case "TOGGLE_BRICK_USER_VISIBILITY": {
      // Toggle userHidden status for specific brick
      if (!action.brickId) return state;

      const targetBrick = state.bricks.get(action.brickId);
      if (!targetBrick) return state;

      const newUserHidden = !targetBrick.userHidden;

      const newBricks = new Map(state.bricks);
      newBricks.set(action.brickId, {
        ...targetBrick,
        userHidden: newUserHidden,
      });

      return { ...state, bricks: newBricks };
    }

    case "TOGGLE_SET_USER_VISIBILITY": {
      // Toggle userHidden status for specific set
      if (!action.setId) return state;

      const targetSet = state.sets.get(action.setId);
      if (!targetSet) return state;

      const newUserHidden = !targetSet.userHidden;

      const newSets = new Map(state.sets);
      newSets.set(action.setId, {
        ...targetSet,
        userHidden: newUserHidden,
      });

      return { ...state, sets: newSets };
    }

    case "SET_BRICK_PACK_BUY_AMOUNT": {
      // Update the selected buy amount for a specific brick pack
      if (!action.brickPackId || action.brickPackBuyAmount === undefined)
        return state;

      const newBrickPackBuyAmounts = new Map(state.brickPackBuyAmounts);
      newBrickPackBuyAmounts.set(action.brickPackId, action.brickPackBuyAmount);

      return { ...state, brickPackBuyAmounts: newBrickPackBuyAmounts };
    }

    case "SET_SET_BUY_AMOUNT": {
      // Update the selected buy amount for a specific set
      if (!action.setId || action.setBuyAmount === undefined) return state;

      const newSetBuyAmounts = new Map(state.setBuyAmounts);
      newSetBuyAmounts.set(action.setId, action.setBuyAmount);

      return { ...state, setBuyAmounts: newSetBuyAmounts };
    }

    case "SET_ACTION_ORDER": {
      // Update the custom action order
      if (!action.actionOrder) return state;

      return { ...state, actionOrder: action.actionOrder };
    }

    case "TOGGLE_EDIT_MODE": {
      // Toggle edit mode for drag-and-reorder (Actions)
      return { ...state, editModeActive: !state.editModeActive };
    }

    case "SET_INVENTORY_ORDER": {
      // Update the custom inventory order
      if (!action.inventoryOrder) return state;

      return { ...state, inventoryOrder: action.inventoryOrder };
    }

    case "TOGGLE_EDIT_MODE_INVENTORY": {
      // Toggle edit mode for drag-and-reorder (Inventory)
      return { ...state, editModeInventory: !state.editModeInventory };
    }

    case "SET_HELP_ICON_ANIMATED": {
      // Set help icon animation state
      return {
        ...state,
        helpIconAnimated: action.helpIconAnimated ?? state.helpIconAnimated,
      };
    }

    //
    //
    // SAVE/LOAD/RESET
    //
    //

    case "SAVE_GAME":
      // Manual save to localStorage
      saveToLocalStorage(state);
      return state; // State unchanged, just side effect

    case "LOAD_GAME":
      // Load from provided save data
      if (action.saveData) {
        const validationError = validateSaveData(action.saveData);
        if (validationError === null) {
          // Valid save data - proceed with loading
          const newState = deserializeState(action.saveData, state);
          // Immediately save to localStorage to persist the uploaded save
          saveToLocalStorage(newState);
          return newState;
        }
        console.error(
          "[Reducer] Invalid save data provided to LOAD_GAME:",
          validationError,
        );
      }
      return state;

    //
    //
    // ACTION BUTTONS
    //
    //

    case "collect_3005":
      return Collect3005("collect_3005", state);

    case "create_3004":
      return CreateBrick_3004("create_3004", state);

    case "create_3010":
      return CreateBrick_3010("create_3010", state);

    case "create_3003":
      return CreateBrick_3003("create_3003", state);

    case "create_3001":
      return CreateBrick_3001("create_3001", state);

    case "split_3005_into_3024":
      return Split3005into3024("split_3005_into_3024", state);

    case "split_3004_into_3023":
      return Split3004into3023("split_3004_into_3023", state);

    case "split_3010_into_3710":
      return Split3010into3710("split_3010_into_3710", state);

    case "split_3003_into_3022":
      return Split3003into3022("split_3003_into_3022", state);

    case "split_3001_into_3020":
      return Split3001into3020("split_3001_into_3020", state);

    case "auto_collect_3005":
      return AutoCollect_3005("auto_collect_3005", state);

    case "auto_collect_3004":
      return AutoCollect_Generic("auto_collect_3004", state);

    case "auto_collect_3024":
      return AutoCollect_Generic("auto_collect_3024", state);

    case "auto_collect_3023":
      return AutoCollect_Generic("auto_collect_3023", state);

    case "auto_collect_3010":
      return AutoCollect_Generic("auto_collect_3010", state);

    case "auto_collect_3003":
      return AutoCollect_Generic("auto_collect_3003", state);

    case "auto_collect_3022":
      return AutoCollect_Generic("auto_collect_3022", state);

    case "auto_collect_3001":
      return AutoCollect_Generic("auto_collect_3001", state);

    case "auto_collect_3710":
      return AutoCollect_Generic("auto_collect_3710", state);

    case "auto_collect_3020":
      return AutoCollect_Generic("auto_collect_3020", state);

    case "increase_capacity_3005":
      return IncreaseCapacity3005("increase_capacity_3005", state);

    case "increase_capacity_3004":
      return IncreaseCapacity3004("increase_capacity_3004", state);

    case "increase_capacity_3023":
      return IncreaseCapacity3023("increase_capacity_3023", state);

    case "increase_capacity_3024":
      return IncreaseCapacityGeneric("increase_capacity_3024", state);

    case "increase_capacity_3010":
      return IncreaseCapacityGeneric("increase_capacity_3010", state);

    case "increase_capacity_3003":
      return IncreaseCapacityGeneric("increase_capacity_3003", state);

    case "increase_capacity_3001":
      return IncreaseCapacityGeneric("increase_capacity_3001", state);

    case "increase_capacity_3022":
      return IncreaseCapacityGeneric("increase_capacity_3022", state);

    case "increase_capacity_3710":
      return IncreaseCapacityGeneric("increase_capacity_3710", state);

    case "increase_capacity_3020":
      return IncreaseCapacityGeneric("increase_capacity_3020", state);

    case "auto_collect_3005_10":
      return AutoCollect_Generic("auto_collect_3005_10", state);

    case "auto_collect_3004_10":
      return AutoCollect_Generic("auto_collect_3004_10", state);

    case "auto_collect_3024_10":
      return AutoCollect_Generic("auto_collect_3024_10", state);

    case "auto_collect_3023_10":
      return AutoCollect_Generic("auto_collect_3023_10", state);

    case "auto_collect_3010_10":
      return AutoCollect_Generic("auto_collect_3010_10", state);

    case "auto_collect_3003_10":
      return AutoCollect_Generic("auto_collect_3003_10", state);

    case "auto_collect_3001_10":
      return AutoCollect_Generic("auto_collect_3001_10", state);

    case "auto_collect_3710_10":
      return AutoCollect_Generic("auto_collect_3710_10", state);

    case "auto_collect_3022_10":
      return AutoCollect_Generic("auto_collect_3022_10", state);

    case "auto_collect_3020_10":
      return AutoCollect_Generic("auto_collect_3020_10", state);

    case "increase_capacity_3005_100":
      return IncreaseCapacityGeneric("increase_capacity_3005_100", state);

    case "increase_capacity_3004_100":
      return IncreaseCapacityGeneric("increase_capacity_3004_100", state);

    case "increase_capacity_3024_100":
      return IncreaseCapacityGeneric("increase_capacity_3024_100", state);

    case "increase_capacity_3023_100":
      return IncreaseCapacityGeneric("increase_capacity_3023_100", state);

    case "increase_capacity_3010_100":
      return IncreaseCapacityGeneric("increase_capacity_3010_100", state);

    case "increase_capacity_3003_100":
      return IncreaseCapacityGeneric("increase_capacity_3003_100", state);

    case "increase_capacity_3001_100":
      return IncreaseCapacityGeneric("increase_capacity_3001_100", state);

    case "increase_capacity_3710_100":
      return IncreaseCapacityGeneric("increase_capacity_3710_100", state);

    case "increase_capacity_3022_100":
      return IncreaseCapacityGeneric("increase_capacity_3022_100", state);

    case "increase_capacity_3020_100":
      return IncreaseCapacityGeneric("increase_capacity_3020_100", state);

    case "increase_capacity_3005_350":
      return IncreaseCapacityGeneric("increase_capacity_3005_350", state);

    case "increase_capacity_3004_350":
      return IncreaseCapacityGeneric("increase_capacity_3004_350", state);

    case "increase_capacity_3024_350":
      return IncreaseCapacityGeneric("increase_capacity_3024_350", state);

    case "increase_capacity_3023_350":
      return IncreaseCapacityGeneric("increase_capacity_3023_350", state);

    case "increase_capacity_3010_350":
      return IncreaseCapacityGeneric("increase_capacity_3010_350", state);

    case "increase_capacity_3003_350":
      return IncreaseCapacityGeneric("increase_capacity_3003_350", state);

    case "increase_capacity_3001_350":
      return IncreaseCapacityGeneric("increase_capacity_3001_350", state);

    case "increase_capacity_3710_350":
      return IncreaseCapacityGeneric("increase_capacity_3710_350", state);

    case "increase_capacity_3022_350":
      return IncreaseCapacityGeneric("increase_capacity_3022_350", state);

    case "increase_capacity_3020_350":
      return IncreaseCapacityGeneric("increase_capacity_3020_350", state);

    case "auto_collect_3005_75":
      return AutoCollect_Generic("auto_collect_3005_75", state);

    case "auto_collect_3004_75":
      return AutoCollect_Generic("auto_collect_3004_75", state);

    case "auto_collect_3024_75":
      return AutoCollect_Generic("auto_collect_3024_75", state);

    case "auto_collect_3023_75":
      return AutoCollect_Generic("auto_collect_3023_75", state);

    case "auto_collect_3010_75":
      return AutoCollect_Generic("auto_collect_3010_75", state);

    case "auto_collect_3003_75":
      return AutoCollect_Generic("auto_collect_3003_75", state);

    case "auto_collect_3001_75":
      return AutoCollect_Generic("auto_collect_3001_75", state);

    case "auto_collect_3710_75":
      return AutoCollect_Generic("auto_collect_3710_75", state);

    case "auto_collect_3022_75":
      return AutoCollect_Generic("auto_collect_3022_75", state);

    case "auto_collect_3020_75":
      return AutoCollect_Generic("auto_collect_3020_75", state);

    case "auto_collect_3005_250":
      return AutoCollect_Generic("auto_collect_3005_250", state);

    case "auto_collect_3004_250":
      return AutoCollect_Generic("auto_collect_3004_250", state);

    case "auto_collect_3024_250":
      return AutoCollect_Generic("auto_collect_3024_250", state);

    case "auto_collect_3023_250":
      return AutoCollect_Generic("auto_collect_3023_250", state);

    case "auto_collect_3010_250":
      return AutoCollect_Generic("auto_collect_3010_250", state);

    case "auto_collect_3003_250":
      return AutoCollect_Generic("auto_collect_3003_250", state);

    case "auto_collect_3001_250":
      return AutoCollect_Generic("auto_collect_3001_250", state);

    case "auto_collect_3710_250":
      return AutoCollect_Generic("auto_collect_3710_250", state);

    case "auto_collect_3022_250":
      return AutoCollect_Generic("auto_collect_3022_250", state);

    case "auto_collect_3020_250":
      return AutoCollect_Generic("auto_collect_3020_250", state);

    case "increase_capacity_3005_1000":
      return IncreaseCapacityGeneric("increase_capacity_3005_1000", state);

    case "increase_capacity_3004_1000":
      return IncreaseCapacityGeneric("increase_capacity_3004_1000", state);

    case "increase_capacity_3024_1000":
      return IncreaseCapacityGeneric("increase_capacity_3024_1000", state);

    case "increase_capacity_3023_1000":
      return IncreaseCapacityGeneric("increase_capacity_3023_1000", state);

    case "increase_capacity_3010_1000":
      return IncreaseCapacityGeneric("increase_capacity_3010_1000", state);

    case "increase_capacity_3003_1000":
      return IncreaseCapacityGeneric("increase_capacity_3003_1000", state);

    case "increase_capacity_3001_1000":
      return IncreaseCapacityGeneric("increase_capacity_3001_1000", state);

    case "increase_capacity_3710_1000":
      return IncreaseCapacityGeneric("increase_capacity_3710_1000", state);

    case "increase_capacity_3022_1000":
      return IncreaseCapacityGeneric("increase_capacity_3022_1000", state);

    case "increase_capacity_3020_1000":
      return IncreaseCapacityGeneric("increase_capacity_3020_1000", state);

    //
    //
    // UPGRADE ACTIONS
    //
    //

    case "upgrade_brickpack_sloped_bricks_part_one":
      return UpgradeGeneric("upgrade_brickpack_sloped_bricks_part_one", state);

    case "upgrade_brickpack_minifig_utensils_part_one":
      return UpgradeGeneric(
        "upgrade_brickpack_minifig_utensils_part_one",
        state,
      );

    case "upgrade_brickpack_bionicles_part_one":
      return UpgradeGeneric("upgrade_brickpack_bionicles_part_one", state);

    case "upgrade_brickpack_animals_part_one":
      return Upgrade_Sets_Generic("upgrade_brickpack_animals_part_one", state);

    case "upgrade_brickpack_minifig_utensils_part_two":
      return Upgrade_Sets_Generic(
        "upgrade_brickpack_minifig_utensils_part_two",
        state,
      );

    case "upgrade_brickpack_bionicles_part_two":
      return Upgrade_Sets_Generic(
        "upgrade_brickpack_bionicles_part_two",
        state,
      );

    case "upgrade_brickpack_circular_bricks":
      return Upgrade_Sets_Generic("upgrade_brickpack_circular_bricks", state);

    case "upgrade_brickpack_minifig_hair":
      return Upgrade_Sets_Generic("upgrade_brickpack_minifig_hair", state);

    case "upgrade_brickpack_technic_bricks":
      return Upgrade_Sets_Generic("upgrade_brickpack_technic_bricks", state);

    case "upgrade_brickpack_minifig_headgear":
      return UpgradeGeneric("upgrade_brickpack_minifig_headgear", state);

    case "increase_capacity_brickpack_sloped_bricks_part_one":
      return IncreaseCapacityGeneric(
        "increase_capacity_brickpack_sloped_bricks_part_one",
        state,
      );

    case "increase_capacity_brickpack_minifig_utensils_part_one":
      return IncreaseCapacityGeneric(
        "increase_capacity_brickpack_minifig_utensils_part_one",
        state,
      );

    case "increase_capacity_brickpack_bionicles_part_one":
      return IncreaseCapacityGeneric(
        "increase_capacity_brickpack_bionicles_part_one",
        state,
      );

    case "increase_capacity_brickpack_animals_part_one":
      return IncreaseCapacityGeneric(
        "increase_capacity_brickpack_animals_part_one",
        state,
      );

    case "increase_capacity_brickpack_minifig_utensils_part_two":
      return IncreaseCapacityGeneric(
        "increase_capacity_brickpack_minifig_utensils_part_two",
        state,
      );

    case "increase_capacity_brickpack_bionicles_part_two":
      return IncreaseCapacityGeneric(
        "increase_capacity_brickpack_bionicles_part_two",
        state,
      );

    case "increase_capacity_brickpack_circular_bricks":
      return IncreaseCapacityGeneric(
        "increase_capacity_brickpack_circular_bricks",
        state,
      );

    case "increase_capacity_brickpack_minifig_hair":
      return IncreaseCapacityGeneric(
        "increase_capacity_brickpack_minifig_hair",
        state,
      );

    case "increase_capacity_brickpack_technic_bricks":
      return IncreaseCapacityGeneric(
        "increase_capacity_brickpack_technic_bricks",
        state,
      );

    case "increase_capacity_brickpack_minifig_headgear":
      return IncreaseCapacityGeneric(
        "increase_capacity_brickpack_minifig_headgear",
        state,
      );

    case "auto_collect_brickpack_sloped_bricks_part_one":
      return AutoCollect_BrickPack_Generic(
        "auto_collect_brickpack_sloped_bricks_part_one",
        state,
      );

    case "auto_collect_brickpack_minifig_utensils_part_one":
      return AutoCollect_BrickPack_Generic(
        "auto_collect_brickpack_minifig_utensils_part_one",
        state,
      );

    case "auto_collect_brickpack_bionicles_part_one":
      return AutoCollect_BrickPack_Generic(
        "auto_collect_brickpack_bionicles_part_one",
        state,
      );

    case "auto_collect_brickpack_animals_part_one":
      return AutoCollect_BrickPack_Generic(
        "auto_collect_brickpack_animals_part_one",
        state,
      );

    case "auto_collect_brickpack_minifig_utensils_part_two":
      return AutoCollect_BrickPack_Generic(
        "auto_collect_brickpack_minifig_utensils_part_two",
        state,
      );

    case "auto_collect_brickpack_bionicles_part_two":
      return AutoCollect_BrickPack_Generic(
        "auto_collect_brickpack_bionicles_part_two",
        state,
      );

    case "auto_collect_brickpack_circular_bricks":
      return AutoCollect_BrickPack_Generic(
        "auto_collect_brickpack_circular_bricks",
        state,
      );

    case "auto_collect_brickpack_minifig_hair":
      return AutoCollect_BrickPack_Generic(
        "auto_collect_brickpack_minifig_hair",
        state,
      );

    case "auto_collect_brickpack_technic_bricks":
      return AutoCollect_BrickPack_Generic(
        "auto_collect_brickpack_technic_bricks",
        state,
      );

    case "auto_collect_brickpack_minifig_headgear":
      return AutoCollect_BrickPack_Generic(
        "auto_collect_brickpack_minifig_headgear",
        state,
      );

    case "increase_capacity_brickpack_sloped_bricks_part_one_75":
      return IncreaseCapacityGeneric(
        "increase_capacity_brickpack_sloped_bricks_part_one_75",
        state,
      );

    case "increase_capacity_brickpack_minifig_utensils_part_one_75":
      return IncreaseCapacityGeneric(
        "increase_capacity_brickpack_minifig_utensils_part_one_75",
        state,
      );

    case "increase_capacity_brickpack_bionicles_part_one_75":
      return IncreaseCapacityGeneric(
        "increase_capacity_brickpack_bionicles_part_one_75",
        state,
      );

    case "auto_collect_brickpack_sloped_bricks_part_one_15":
      return AutoCollect_BrickPack_Generic(
        "auto_collect_brickpack_sloped_bricks_part_one_15",
        state,
      );

    case "auto_collect_brickpack_minifig_utensils_part_one_15":
      return AutoCollect_BrickPack_Generic(
        "auto_collect_brickpack_minifig_utensils_part_one_15",
        state,
      );

    case "auto_collect_brickpack_bionicles_part_one_15":
      return AutoCollect_BrickPack_Generic(
        "auto_collect_brickpack_bionicles_part_one_15",
        state,
      );

    case "auto_collect_brickpack_sloped_bricks_part_one_75":
      return AutoCollect_BrickPack_Generic(
        "auto_collect_brickpack_sloped_bricks_part_one_75",
        state,
      );

    case "auto_collect_brickpack_minifig_utensils_part_one_75":
      return AutoCollect_BrickPack_Generic(
        "auto_collect_brickpack_minifig_utensils_part_one_75",
        state,
      );

    case "auto_collect_brickpack_bionicles_part_one_75":
      return AutoCollect_BrickPack_Generic(
        "auto_collect_brickpack_bionicles_part_one_75",
        state,
      );

    case "auto_collect_brickpack_animals_part_one_75":
      return AutoCollect_BrickPack_Generic(
        "auto_collect_brickpack_animals_part_one_75",
        state,
      );

    case "auto_collect_brickpack_minifig_utensils_part_two_75":
      return AutoCollect_BrickPack_Generic(
        "auto_collect_brickpack_minifig_utensils_part_two_75",
        state,
      );

    case "auto_collect_brickpack_bionicles_part_two_75":
      return AutoCollect_BrickPack_Generic(
        "auto_collect_brickpack_bionicles_part_two_75",
        state,
      );

    case "increase_capacity_brickpack_animals_part_one_75":
      return IncreaseCapacityGeneric(
        "increase_capacity_brickpack_animals_part_one_75",
        state,
      );

    case "increase_capacity_brickpack_minifig_utensils_part_two_75":
      return IncreaseCapacityGeneric(
        "increase_capacity_brickpack_minifig_utensils_part_two_75",
        state,
      );

    case "increase_capacity_brickpack_bionicles_part_two_75":
      return IncreaseCapacityGeneric(
        "increase_capacity_brickpack_bionicles_part_two_75",
        state,
      );

    case "increase_capacity_brickpack_circular_bricks_75":
      return IncreaseCapacityGeneric(
        "increase_capacity_brickpack_circular_bricks_75",
        state,
      );

    case "increase_capacity_brickpack_minifig_hair_75":
      return IncreaseCapacityGeneric(
        "increase_capacity_brickpack_minifig_hair_75",
        state,
      );

    case "increase_capacity_brickpack_technic_bricks_75":
      return IncreaseCapacityGeneric(
        "increase_capacity_brickpack_technic_bricks_75",
        state,
      );

    case "increase_capacity_brickpack_minifig_headgear_75":
      return IncreaseCapacityGeneric(
        "increase_capacity_brickpack_minifig_headgear_75",
        state,
      );

    case "auto_collect_brickpack_animals_part_one_15":
      return AutoCollect_BrickPack_Generic(
        "auto_collect_brickpack_animals_part_one_15",
        state,
      );

    case "auto_collect_brickpack_minifig_utensils_part_two_15":
      return AutoCollect_BrickPack_Generic(
        "auto_collect_brickpack_minifig_utensils_part_two_15",
        state,
      );

    case "auto_collect_brickpack_bionicles_part_two_15":
      return AutoCollect_BrickPack_Generic(
        "auto_collect_brickpack_bionicles_part_two_15",
        state,
      );

    case "auto_collect_brickpack_circular_bricks_15":
      return AutoCollect_BrickPack_Generic(
        "auto_collect_brickpack_circular_bricks_15",
        state,
      );

    case "auto_collect_brickpack_minifig_hair_15":
      return AutoCollect_BrickPack_Generic(
        "auto_collect_brickpack_minifig_hair_15",
        state,
      );

    case "auto_collect_brickpack_technic_bricks_15":
      return AutoCollect_BrickPack_Generic(
        "auto_collect_brickpack_technic_bricks_15",
        state,
      );

    case "auto_collect_brickpack_minifig_headgear_15":
      return AutoCollect_BrickPack_Generic(
        "auto_collect_brickpack_minifig_headgear_15",
        state,
      );

    //
    //
    // BRICK PACK PURCHASES
    //
    //

    case "buy_brick_pack":
      return BuyBrickPack(
        action.brickPackId ?? "invalid",
        state,
        action.buyAmount ?? 0,
      );

    //
    //
    // SET PURCHASES
    //
    //

    case "buy_set_6772-1":
      return BuySetGeneric("buy_set_6772-1", state, action.buyAmount ?? 1);

    case "buy_set_8534-1":
      return BuySetGeneric("buy_set_8534-1", state, action.buyAmount ?? 1);

    case "buy_set_4866-1":
      return BuySetGeneric("buy_set_4866-1", state, action.buyAmount ?? 1);

    case "buy_set_10307-1":
      return BuySetGeneric("buy_set_10307-1", state, action.buyAmount ?? 1);

    case "buy_set_4432-1":
      return BuySetGeneric("buy_set_4432-1", state, action.buyAmount ?? 1);

    case "buy_set_21060-1":
      return BuySetGeneric("buy_set_21060-1", state, action.buyAmount ?? 1);

    case "buy_set_7146-1":
      return BuySetGeneric("buy_set_7146-1", state, action.buyAmount ?? 1);

    case "buy_set_71766-1":
      return BuySetGeneric("buy_set_71766-1", state, action.buyAmount ?? 1);

    case "buy_set_10305-1":
      return BuySetGeneric("buy_set_10305-1", state, action.buyAmount ?? 1);

    case "buy_set_10179-1":
      return BuySetGeneric("buy_set_10179-1", state, action.buyAmount ?? 1);

    //
    //
    // INVALID / BASE CASE
    //
    //

    case "invalid":
    default:
      return state;
  }
};
